import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { guideFor, placesIn, verdictFor, CITY_GUIDES } from '../data/guide';
import { SHOP_PRODUCTS } from '../data/shop';
import { formatPrice } from '../data/smaken';
import { PlaceCard } from '../components/PlaceCard';
import { Rating } from '../components/Rating';
import { Reveal } from '../components/Reveal';
import { Note, ArrowMark } from '../components/Marks';
export function CityGuide() {
  const {
    city
  } = useParams();
  const guide = guideFor(city ?? '');
  if (!guide) return <Navigate to="/guide" replace data-id="element-850" data-mp="30c82hie6976x" />;
  const places = placesIn(guide.city);
  const products = SHOP_PRODUCTS.filter(p => p.city === guide.city).slice(0, 3);
  const others = CITY_GUIDES.filter(g => g.city !== guide.city);
  return <article data-id="element-851" data-mp="3syst2gmtpf9f">
      <div className="mx-auto max-w-page px-5 pt-8 md:px-8" data-id="element-852" data-mp="3sy9en9wdt905">
        <Link to="/guide" className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide2 text-black/50 transition-colors duration-150 ease-out hover:text-rod" data-id="element-853" data-mp="1mq1lncvztcl">
          <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-854" data-mp="1qcuzh1gfo7el" />
          Guiden
        </Link>

        <div className="mt-6 grid items-end gap-8 lg:grid-cols-[minmax(0,1.1fr),minmax(0,1fr)]" data-id="element-855" data-mp="251r5yu2udcd3">
          <div data-id="element-856" data-mp="207wozyemfbhp">
            <h1 className="display-xl text-[3.2rem] uppercase leading-[0.9] sm:text-[5rem]" data-id="element-857" data-mp="207ej0a25piwl">
              {guide.city}
            </h1>
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-black/70" data-id="element-858" data-mp="292aytlgdtttf">{guide.intro}</p>
            <Note className="mt-4 block" data-id="element-859" data-mp="2nzlkfnmqog2v">{guide.strap}.</Note>
          </div>
          <img src={guide.image} alt={`Mat och miljö från ${guide.city}`} className="aspect-[16/10] w-full object-cover" data-id="element-860" data-mp="2nwpizpbon99o" />
        </div>
      </div>

      {/* A perfect day */}
      <section aria-labelledby="dagen" className="mt-16 border-y-2 border-black" data-id="element-861" data-mp="omhgjib7lmut">
        <div className="mx-auto max-w-page px-5 py-14 md:px-8 md:py-16" data-id="element-862" data-mp="1ea3ej07m0gv3">
          <h2 id="dagen" className="display-xl text-4xl uppercase sm:text-5xl" data-id="element-863" data-mp="1ec6cq7ix1gaw">
            En perfekt dag i {guide.city}
          </h2>

          <ol className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-864" data-mp="1nb1lnrdch4hw">
            {guide.perfectDay.map((stop, i) => <Reveal key={stop.time} delay={Math.min(i * 0.06, 0.18)} data-id="element-865" data-mp="1saw7hgi9j3w3" data-mpi={i}>
                <li className="border-t border-black/20 pt-4" data-id="element-866" data-mp="1x9hb7pwq45pa">
                  <span className="display-xl block text-3xl text-rod" data-id="element-867" data-mp="1x6v39p5x7o6j">{stop.time}</span>
                  <p className="mt-3 font-display text-xl leading-snug" data-id="element-868" data-mp="218q64y3i29mf">{stop.what}</p>
                  {stop.slug ? <Link to={`/plats/${stop.slug}`} className="mt-1.5 inline-block text-[11px] font-bold uppercase tracking-wide2 text-blaa transition-colors duration-150 ease-out hover:text-rod" data-id="element-869" data-mp="263lzq2odx51n">
                      {stop.where}
                    </Link> : <p className="mt-1.5 text-[11px] font-bold uppercase tracking-wide2 text-black/45" data-id="element-870" data-mp="9c87d18559kd">
                      {stop.where}
                    </p>}
                </li>
              </Reveal>)}
          </ol>
        </div>
      </section>

      {/* Places with verdicts */}
      <section aria-labelledby="stallen" className="mx-auto max-w-page px-5 py-16 md:px-8 md:py-20" data-id="element-871" data-mp="3qkv53j1hbuhx">
        <h2 id="stallen" className="display-xl text-4xl uppercase sm:text-6xl" data-id="element-872" data-mp="s14w6pkrr9v7">
          Ställena
        </h2>

        <div className="mt-10 space-y-10" data-id="element-873" data-mp="n4ob648v9bmc">
          {places.map((place, i) => {
          const verdict = verdictFor(place.slug);
          return <Reveal key={place.slug} delay={Math.min(i * 0.05, 0.15)} data-id="element-874" data-mp="dbqpjp8kv6ji" data-mpi={i}>
                <div className="grid gap-6 border-t-2 border-black pt-6 lg:grid-cols-[minmax(0,1fr),minmax(0,1.4fr)]" data-id="element-875" data-mp="1arw73vken8bp">
                  <div className="max-w-sm" data-id="element-876" data-mp="1617gsjkgohe5">
                    <PlaceCard place={place} data-id="element-877" data-mp="15zct4xht2b6d" />
                  </div>
                  {verdict && <div className="lg:pt-4" data-id="element-878" data-mp="qlskr93ma19z">
                      <Rating value={verdict.rating} data-id="element-879" data-mp="viso3c930pxe" />
                      <p className="mt-4 font-display text-2xl leading-snug sm:text-3xl" data-id="element-880" data-mp="vg69isnrxa5o">
                        {verdict.verdict}
                      </p>
                      <p className="mt-4 border-l-2 border-rod pl-5 font-hand text-2xl text-rod" data-id="element-881" data-mp="ll4itsduo00z">
                        Beställ: {verdict.order}
                      </p>
                    </div>}
                </div>
              </Reveal>;
        })}
        </div>
      </section>

      {/* Take it home */}
      {products.length > 0 && <section aria-labelledby="hem" className="bg-blaa text-papper" data-id="element-882" data-mp="14ipbzbnndkre">
          <div className="mx-auto max-w-page px-5 py-16 md:px-8 md:py-20" data-id="element-883" data-mp="1s8ry7b4vrwi3">
            <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-884" data-mp="1s6oyfzdxep06">
              <div data-id="element-885" data-mp="1x09x7tncfn0f">
                <h2 id="hem" className="display-xl text-4xl uppercase sm:text-5xl" data-id="element-886" data-mp="1x1l2xz0cn4u6">
                  Ta med {guide.city} hem
                </h2>
                <p className="mt-3 text-papper/70" data-id="element-887" data-mp="3crc5smvrvlnd">Samma producenter, levererat till dörren.</p>
              </div>
              <Link to="/butik" className="group flex items-center gap-2 bg-papper px-7 py-4 text-[12px] font-bold uppercase tracking-wide2 text-black transition-colors duration-150 ease-out hover:bg-smor" data-id="element-888" data-mp="3reoxyug34pmq">
                Till butiken
                <ArrowMark className="h-3.5 w-8 transition-transform duration-200 ease-out group-hover:translate-x-1" data-id="element-889" data-mp="2tsrkjbgkbahn" />
              </Link>
            </div>

            <ul className="mt-12 grid gap-x-8 gap-y-10 sm:grid-cols-3" data-id="element-890" data-mp="37pmxvg3ujo3i">
              {products.map(product => <li key={product.id} className="border-t border-papper/30 pt-4" data-id="element-891" data-mp="n1ifiy90pvbb">
                  <div className="aspect-[4/3] overflow-hidden" data-id="element-892" data-mp="n3cza98jzhdt">
                    <img src={product.image} alt="" loading="lazy" className="h-full w-full object-cover" data-id="element-893" data-mp="iz5akc23i1fl" />
                  </div>
                  <h3 className="mt-4 font-display text-xl" data-id="element-894" data-mp="3bwrlwkbu5039">{product.name}</h3>
                  <p className="mt-1 text-[11px] font-bold uppercase tracking-wide2 text-papper/55" data-id="element-895" data-mp="3grneblkzhpgw">
                    {formatPrice(product.price)} · {product.unit}
                  </p>
                </li>)}
            </ul>
          </div>
        </section>}

      {/* Other cities */}
      <section aria-labelledby="andra" className="mx-auto max-w-page px-5 py-16 md:px-8" data-id="element-896" data-mp="2t5qlrsyzayi4">
        <h2 id="andra" className="text-[11px] font-bold uppercase tracking-wide3 text-black/45" data-id="element-897" data-mp="2y38ov2qbeji7">
          Andra städer
        </h2>
        <div className="mt-5 flex flex-wrap gap-3" data-id="element-898" data-mp="36vancvgtpppu">
          {others.map(other => <Link key={other.city} to={`/guide/${other.city.toLowerCase()}`} className="border border-black/20 px-6 py-3 font-display text-xl uppercase transition-colors duration-150 ease-out hover:border-black hover:bg-black hover:text-papper" data-id="element-899" data-mp="36x5b0hjhbvxm">
              {other.city}
            </Link>)}
        </div>
      </section>
    </article>;
}